import { useEffect, useRef, useState } from 'react';
import lottie, { type AnimationItem } from 'lottie-web';
import { ungzip } from 'pako';

type Props = {
  url: string;
};

/**
 * Telegram animated stickers are gzipped Lottie (.tgs). Decode and play with lottie-web.
 */
export function TgsSticker({ url }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return undefined;

    let anim: AnimationItem | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        const buf = await res.arrayBuffer();
        const jsonText = ungzip(new Uint8Array(buf), { to: 'string' });
        const data = JSON.parse(jsonText) as object;
        if (cancelled || !containerRef.current) return;
        anim = lottie.loadAnimation({
          container: host,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: data,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
          },
        });
        requestAnimationFrame(() => {
          if (cancelled || !anim) return;
          anim.resize();
        });
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      anim?.destroy();
    };
  }, [url]);

  if (error) {
    return (
      <div className="rounded-xl px-3 py-2 text-xs text-[#7A818C]" style={{ background: 'rgba(241,243,246,0.95)' }}>
        Sticker preview unavailable
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex h-[200px] w-[200px] shrink-0 items-center justify-center overflow-hidden rounded-[18px]"
      aria-label="Animated sticker"
    />
  );
}
